import { useMutation } from "react-query";
import type { HashMap } from "@certa/types";
import { genericFetch, getDesignAIAPIOrigin } from "@certa/network";
import type { UseMutationExtraConfigType } from "@certa/common/types";
import type { ExtraAPIConfigPayload } from "./types/commonPayload.types";
import { getCommonHeaders } from "./utils/headers.utils";
import { cancelExecutionAbortController } from "./utils/abortController.utils";
import type { IntentType } from "./types/intent.types";

// TYPES
export type InteractionFulfillmentRequestPayload = {
  // Old method of polling subtask progress
  subtaskId?: string;
  // Old method of polling subtask progress
  // - used for backward compatibility with '/' commands
  // - interaction_id is returned from 'interaction/' API
  interactionId?: string;
};

export enum TargetType {
  STEP = "step",
  FIELD = "field",
  STEPGROUP = "stepgroup",
  TEMPLATE = "template"
}

export enum ReasonCode {
  /** User mentioned a component X and system didn't find a component with the name X at all. */
  NOT_FOUND = "not_found",
  /** User mentioned a component but multiple possible matches were found */
  MULTIPLE_FOUND = "multiple_found",
  /**
   * User mentioned a component but additional context is required.
   * E.g. In the case of "Add a field" prompt, system might ask for field type or name of a component
   * */
  MISSING_INFO = "missing_info"
}

export type RequiredContextPayload = {
  targetType: TargetType;
  name: string;
  reasonCode: ReasonCode;
  possibleMatches: string[] | null;
  kind: string;
  /** Will be true when BE explicitly wants to initiate free-text follow-up */
  requireFreeTextFollowup?: boolean;
};

export enum MetaDataItemStatus {
  SUCCESS = "success",
  CONTEXT_REQUIRED = "context_required",
  FAILED = "failed",
  NO_OP = "no_operation"
}

export type InteractionMetadata = {
  userInput: string;
  intent: IntentType;
  data: any;
  status: MetaDataItemStatus;
  message: string | null;
  requiredContext: RequiredContextPayload | null;
};

export enum FulfillmentStatus {
  SUCCESS = "success",
  PARTIAL_SUCCESS = "partial_success",
  FAILED = "failed",
  PROCESSING = "processing",
  NOT_SCHEDULED = "not_scheduled"
}

export type InteractionFulfillmentProcessingResponse = {
  code: FulfillmentStatus.PROCESSING | FulfillmentStatus.NOT_SCHEDULED;
};

export type InteractionFulfillmentFailureResponse = {
  code: FulfillmentStatus.FAILED;
  message: string;
};

export enum ChangeAction {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete"
}

export enum ChangeLevel {
  PROCESS = "process",
  SWIMLANE = "swimlane",
  STEP = "step",
  FIELD = "field",
  EVENT_RULES = "event_rules",
  APP_SETTINGS = "app_settings",
  RECORDS = "records"
}

export type ChangeDiff = {
  action: ChangeAction;
  level: ChangeLevel;
  setting?: string;
  uniqueId?: string;
  tag: string;
  parentTag?: string;
  data?: HashMap;
  path?: string;
  changedData?: {
    added?: string[];
    updated?: string[];
    removed?: string[];
  };
  atIndex?: number;
};

export type ChangeDiffWithoutData = Omit<ChangeDiff, "data"> & { data?: never };

export type InteractionFulfillmentSuccessResponse = {
  code: FulfillmentStatus.SUCCESS | FulfillmentStatus.PARTIAL_SUCCESS;
  interactionId: string;
  message: string;
  metadata: InteractionMetadata[];
  changes: ChangeDiff[];
  taskId: string;
  sessionId: string;
};

export type InteractionFulfillmentResponse =
  | InteractionFulfillmentProcessingResponse
  | InteractionFulfillmentFailureResponse
  | InteractionFulfillmentSuccessResponse;

// SERVICE
const getInteractionFulfillment = async (
  payload: InteractionFulfillmentRequestPayload & ExtraAPIConfigPayload
): Promise<InteractionFulfillmentResponse> => {
  const parsedPayload = payload.interactionId
    ? {
        interaction_id: payload.interactionId
      }
    : {
        sub_task_id: payload.subtaskId
      };

  const requestOptions: RequestInit = {
    method: "POST",
    headers: getCommonHeaders({ userName: payload.userName }),
    credentials: "include",
    body: JSON.stringify(parsedPayload),
    signal: cancelExecutionAbortController.signal
  };

  const response = await genericFetch(
    `${getDesignAIAPIOrigin()}/interaction/status`,
    requestOptions
  );
  return interactionFulfillmentModelCreator(response);
};

// MODELS
export const interactionFulfillmentModelCreator = (
  response: any
): InteractionFulfillmentResponse => {
  if (
    response.code === FulfillmentStatus.PROCESSING ||
    response.code === FulfillmentStatus.NOT_SCHEDULED
  ) {
    return interactionFulfillmentProcessingModelCreator(response);
  } else if (response.code === FulfillmentStatus.FAILED) {
    return interactionFulfillmentFailureModelCreator(response);
  } else if (
    response.code === FulfillmentStatus.SUCCESS ||
    response.code === FulfillmentStatus.PARTIAL_SUCCESS
  ) {
    return interactionFulfillmentSuccessModelCreator(response);
  } else {
    throw new Error("Invalid response code");
  }
};

const interactionFulfillmentProcessingModelCreator = (
  response: any
): InteractionFulfillmentProcessingResponse => ({
  code: response.code
});

const interactionFulfillmentFailureModelCreator = (
  response: any
): InteractionFulfillmentFailureResponse => ({
  code: response.code,
  message: response.message
});

const interactionFulfillmentSuccessModelCreator = (
  response: any
): InteractionFulfillmentSuccessResponse => ({
  code: response.code,
  interactionId: response.interaction_id,
  message: response.message,
  metadata: response.metadata?.map(interactionMetadataModelCreator),
  changes: response.changes?.map(deployableJSONChangeModelCreator),
  taskId: response.task_id,
  sessionId: response.session_id
});

const interactionMetadataModelCreator = (
  response: any
): InteractionMetadata => ({
  userInput: response.user_input,
  intent: response.intent,
  data: response.data,
  status: response.status,
  message: response.message,
  requiredContext: response.required_context
    ? {
        targetType: response.required_context.target_type,
        name: response.required_context.name,
        reasonCode: response.required_context.reason_code,
        possibleMatches: response.required_context.possible_matches,
        kind: response.required_context.kind,
        requireFreeTextFollowup:
          response.required_context.require_free_text_followup
      }
    : null
});

export const deployableJSONChangeModelCreator = (
  response: any
): ChangeDiff => ({
  action: response.action,
  level: response.level,
  tag: response.tag,
  parentTag: response.parent_tag,
  atIndex: response.at_index,
  data: response.data,
  changedData: response.changes,
  path: response.path,
  setting: response.setting,
  uniqueId: response.unique_id
});

// HOOK
export const useInteractionFulfillment = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getInteractionFulfillment, extraConfig);
